<template>
  <div>
    <p v-if="showDescription" class="mb-6">
      {{ $t('Bokvalitetsrosen.PlansPricing.description') }}
    </p>
    <div
      class="pricing-plans flex flex-col md:flex-row gap-6 justify-start relative"
      :class="{ 'with-starburst': showStarburst }"
    >
      <BaseCard
        v-for="plan in plans"
        :key="plan.id"
        class="flex-1 min-w-[260px]"
      >
        <template #default>
          <div class="h-full flex flex-col items-center py-6">
            <h2 class="text-xl font-semibold mb-2">{{ plan.title }}</h2>
            <div class="text-3xl font-bold">{{ plan.price }}</div>
            <div class="flex-grow my-12">
              <ul class="mb-4 text-sm text-left w-full px-4">
                <li
                  v-for="feature in plan.features"
                  :key="feature"
                  class="mb-1 flex items-center"
                >
                  <span
                    v-if="feature !== ''"
                    class="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"
                  ></span>
                  {{ feature }}
                </li>
              </ul>
            </div>
            <div>
              <router-link
                v-if="ctaMode === 'link'"
                :to="ctaLink"
                class="inline-flex w-full items-center justify-center bg-black px-6 py-2 text-white transition hover:bg-gray-800"
              >
                {{ $t('Bokvalitetsrosen.PlansPricing.choose_button') }}
              </router-link>
              <button
                v-else
                class="bg-black text-white px-6 py-2 hover:bg-gray-800 transition w-full"
                @click="$emit('select-plan', plan)"
              >
                {{ $t('Bokvalitetsrosen.PlansPricing.choose_button') }}
              </button>
            </div>
          </div>
        </template>
      </BaseCard>
      <div v-if="showStarburst" class="special-offer-starburst">
        <div class="starburst-content">
          <span class="starburst-title">Testbruker</span>
          <span class="starburst-text">
            Vil du teste verktøyet gratis én måned - ta kontakt på
            <span class="starburst-email">post<wbr>@<wbr>bokvalitetsrosen<wbr>.<wbr>no.</span>
          </span>
        </div>
      </div>
    </div>

    <ul class="mt-6 mb-10">
      <li>
        Vil du teste verktøyet i én måned? Ta kontakt på
        <a href="mailto:post@bokvalitetsrosen.no" class="text-blue-600 hover:text-blue-800">
          post@bokvalitetsrosen.no
        </a>
        for avtale om oppstart og oppfølging.
      </li>
      <li>
        Alle abonnement gjelder for ett år og fornyes automatisk med mindre de sies opp.
        Oppsigelse må sendes skriftlig til
        <a href="mailto:post@bokvalitetsrosen.no" class="text-blue-600 hover:text-blue-800">
          post@bokvalitetsrosen.no
        </a>
      </li>
      <li>Betaling skjer via faktura.</li>
      <li>Ved behov for mer enn fem brukere per organisasjon, tilkommer en ekstra kostnad.</li>
      <li>
        Les våre
        <router-link
          to="/license-terms"
          target="_blank"
          class="text-blue-600 hover:text-blue-800 underline"
        >
          kjøpsvilkår
        </router-link>
        for mer informasjon.
      </li>
    </ul>
  </div>
</template>

<script>
import BaseCard from '@/components/shared/BaseCard.vue'

export default {
  name: 'PricingPlans',
  components: { BaseCard },
  emits: ['select-plan'],
  props: {
    showDescription: {
      type: Boolean,
      default: true,
    },
    ctaMode: {
      type: String,
      default: 'modal',
      validator: value => ['modal', 'link'].includes(value),
    },
    ctaLink: {
      type: String,
      default: '/pricing',
    },
    showStarburst: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    plans () {
      return [
        {
          id: 'member',
          title: this.$t('Bokvalitetsrosen.PlansPricing.plans_member_title'),
          price: this.$t('Bokvalitetsrosen.PlansPricing.plans_member_price'),
          description: this.$t('Bokvalitetsrosen.PlansPricing.plans_member_description'),
          manchet: this.$t('Bokvalitetsrosen.PlansPricing.plans_member_manchet'),
          features: [
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_1'),
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_2'),
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_3'),
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_4'),
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_5'),
          ],
        },
        {
          id: 'non-member',
          title: this.$t('Bokvalitetsrosen.PlansPricing.plans_non_member_title'),
          price: this.$t('Bokvalitetsrosen.PlansPricing.plans_non_member_price'),
          description: this.$t('Bokvalitetsrosen.PlansPricing.plans_non_member_description'),
          manchet: this.$t('Bokvalitetsrosen.PlansPricing.plans_non_member_manchet'),
          features: [
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_1'),
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_2'),
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_3'),
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_4'),
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_5'),
          ],
        },
        {
          id: 'municipality',
          title: this.$t('Bokvalitetsrosen.PlansPricing.plans_municipality_title'),
          price: this.$t('Bokvalitetsrosen.PlansPricing.plans_municipality_price'),
          description: this.$t('Bokvalitetsrosen.PlansPricing.plans_municipality_description'),
          manchet: this.$t('Bokvalitetsrosen.PlansPricing.plans_municipality_manchet'),
          features: [
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_1'),
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_2'),
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_3'),
            this.$t('Bokvalitetsrosen.PlansPricing.plans_member_features_feature_6'),
          ],
        },
      ]
    },
  },
}
</script>

<style scoped>
ul {
  list-style-type: disc;
  padding-left: 2rem;
}

.special-offer-starburst {
  position: absolute;
  bottom: 40px;
  left: 22%;
  width: 280px;
  height: 280px;
  background: #fff600;
  clip-path: polygon(
    50% 0%,
    56% 20%,
    71% 6%,
    68% 27%,
    88% 19%,
    77% 37%,
    98% 38%,
    82% 50%,
    98% 62%,
    77% 63%,
    88% 81%,
    68% 73%,
    71% 94%,
    56% 80%,
    50% 100%,
    44% 80%,
    29% 94%,
    32% 73%,
    12% 81%,
    23% 63%,
    2% 62%,
    18% 50%,
    2% 38%,
    23% 37%,
    12% 19%,
    32% 27%,
    29% 6%,
    44% 20%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(-15deg);
  z-index: 100;
  cursor: default;
}

@media (min-width: 768px) and (max-width: 1200px) {
  .pricing-plans.with-starburst {
    padding-top: 140px;
  }

  .special-offer-starburst {
    top: 0;
    left: calc(33.333% - 105px);
    right: auto;
    bottom: auto;
    width: 210px;
    height: 210px;
    transform: rotate(-10deg);
  }

  .starburst-content {
    padding: 36px 16px;
    max-width: 130px;
  }

  .starburst-title {
    font-size: 1rem;
    margin-bottom: 6px;
  }

  .starburst-text {
    font-size: 0.72rem;
    line-height: 1.2;
  }
}

.starburst-content {
  text-align: center;
  padding: 20px 20px;
  max-width: 200px;
}

.starburst-title {
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: #000;
}

.starburst-text {
  display: block;
  font-size: 0.85rem;
  line-height: 1.3;
  color: #000;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.starburst-email {
  overflow-wrap: anywhere;
  word-break: break-word;
}

@media (max-width: 768px) {
  .pricing-plans.with-starburst {
    padding-top: 140px;
  }

  .special-offer-starburst {
    position: absolute;
    top: 8px;
    right: 8px;
    left: auto;
    bottom: auto;
    width: 210px;
    height: 210px;
    margin: 0;
    transform: rotate(-10deg);
  }

  .starburst-content {
    padding: 36px 16px;
    max-width: 130px;
  }

  .starburst-title {
    font-size: 1rem;
    margin-bottom: 6px;
  }

  .starburst-text {
    font-size: 0.72rem;
    line-height: 1.2;
  }
}
</style>
